import type { MutableRefObject } from 'react';
import type { ThemeTokens } from '@/shared/ui/theme/tokens';
import { C } from '@/shared/ui/theme/tokens';
import { FaceFrame } from './FaceFrame';

interface CameraFeedProps {
  scanning: boolean;
  size: number;
  T: ThemeTokens;
  /** When provided and `cameraActive=true`, renders a real <video> element. */
  videoRef?: MutableRefObject<HTMLVideoElement | null>;
  /** When true, hides the SVG avatar and shows the live video stream. */
  cameraActive?: boolean;
  /** When true and `cameraActive=false`, shows a loading spinner instead of a blank circle. */
  showSpinner?: boolean;
}

export function CameraFeed({ scanning, size, T, videoRef, cameraActive = false, showSpinner = false }: CameraFeedProps) {
  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        background: T.camBg,
        boxShadow: `0 0 0 1.5px #2485F4, 0 0 0 4px ${C.primary}44, 0 16px 56px rgba(0,0,0,.45)`,
      }}
    >
      {/* Live video — always in DOM when videoRef provided so the ref is populated.
          Visibility controlled by cameraActive to avoid the ref=null chicken-and-egg bug. */}
      {videoRef && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            position:     'absolute',
            inset:        0,
            width:        '100%',
            height:       '100%',
            objectFit:    'cover',
            borderRadius: '50%',
            transform:    'scaleX(-1)',   // selfie mirror
            pointerEvents: 'none',
            zIndex:       1,
            visibility:   cameraActive ? 'visible' : 'hidden',
          }}
        />
      )}

      {/* Spinner — shown only during camera-prompt phase */}
      {!cameraActive && showSpinner && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            className="mi-anim-spin"
            style={{
              width: 40,
              height: 40,
              border: '4px solid rgba(255,255,255,0.15)',
              borderTopColor: C.primary,
              borderRadius: '50%',
            }}
          />
        </div>
      )}

      {scanning && (
        <div
          className="mi-anim-scan-line"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, transparent, ${C.primary}cc, transparent)`,
            zIndex: 4,
          }}
        />
      )}
      <FaceFrame size={size} active={scanning} />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: 'radial-gradient(circle,transparent 48%,rgba(0,0,0,.55) 100%)',
          pointerEvents: 'none',
          zIndex: 3,
        }}
      />
    </div>
  );
}
